import {useMutation, useQuery, useQueryClient} from "react-query";
import axios, {AxiosError} from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";
import {RowTeleObjectData} from "../../../Shared/Types/TeleObject";

const apiUrl = "api/teleobject";
const TELE_OBJECT_GRID_QUERY_KEY = "tele-object-grid";

export interface TeleObjectToSave {
    id?: number | null;
    status: string;
    equipment: {
        id: number;
        ets?: number | null;
    };
    teleObjectConfig: {
        id: number;
    };
    teleObjectPosition?: number | null;
    teleObjectSpecificationType: {
        id: number;
    };
}

export interface TeleObjectGridRow extends RowTeleObjectData {
    id?: number | null;
    teleObjectSpecificationTypeName?: string;
    teleObjectDataTypeName?: string;
}

export interface TeleObjectGridPage {
    data: Array<TeleObjectGridRow>;
    totalOfRecords: number;
}

interface ProblemResponse {
    title?: string;
    detail?: string;
}

type ApiError = AxiosError<ProblemResponse>;

const CONFLICT_STATUS = 409;

const NOT_FOUND_STATUS = 404;

const extractErrorMessage = (error: ApiError, fallback: string): string =>
    error?.response?.data?.detail ?? error?.message ?? fallback;

const DELETE_ERROR_DURATION = 1500;

const DELETE_ERROR_MESSAGE = "Erro ao excluir Teleobjeto. Tente novamente.";
const TELE_OBJECT_IN_USE_MESSAGE = "O teleobjeto não pode ser excluído porque já está em uso.";
const TELE_OBJECTS_IN_USE_MESSAGE = "Um ou mais teleobjetos não podem ser excluídos porque já estão em uso.";
const TELE_OBJECT_NOT_FOUND_MESSAGE = "O teleobjeto não foi encontrado. A listagem foi atualizada.";
const TELE_OBJECTS_NOT_FOUND_MESSAGE = "Um ou mais teleobjetos não foram encontrados. A listagem foi atualizada.";

const extractDeleteErrorMessage = (error: ApiError, quantity: number): string => {
    if (error?.response?.status === CONFLICT_STATUS) {
        return quantity > 1 ? TELE_OBJECTS_IN_USE_MESSAGE : TELE_OBJECT_IN_USE_MESSAGE;
    }
    if (error?.response?.status === NOT_FOUND_STATUS) {
        return quantity > 1 ? TELE_OBJECTS_NOT_FOUND_MESSAGE : TELE_OBJECT_NOT_FOUND_MESSAGE;
    }
    return DELETE_ERROR_MESSAGE;
};

const fetchTeleObject = async (pagedSearchParams: PagedSearchParams): Promise<TeleObjectGridPage> => {
    return await axios
        .get<TeleObjectGridPage>(apiUrl + "/grid/find-all", {
            params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null,
            timeout: 30000
        })
        .then((res) => res.data);
};

export const TeleObjectReactQuery = (pagedSearchParams: PagedSearchParams) => {
    return useQuery<TeleObjectGridPage, ApiError>({
        queryKey: [TELE_OBJECT_GRID_QUERY_KEY, pagedSearchParams],
        queryFn: () => {
            return fetchTeleObject(pagedSearchParams);
        }
    });
};

const saveTeleObject = async (data: TeleObjectToSave) => {
    if (data.id) {
        return await axios.put(`${apiUrl}/${data.id}`, data, {timeout: 30000}).then((res) => res.data);
    }
    return await axios.post(apiUrl, data, {timeout: 30000}).then((res) => res.data);
};

export const SaveTeleObjectReactQuery = () => {
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation<unknown, ApiError, TeleObjectToSave>({
        mutationFn: (data: TeleObjectToSave) => {
            return saveTeleObject(data);
        },
        onSuccess: (result, variables) => {
            enqueueSnackbar(
                variables.id ? "Teleobjeto atualizado com sucesso!" : "Teleobjeto criado com sucesso!",
                {variant: "success"}
            );
            queryClient.invalidateQueries([TELE_OBJECT_GRID_QUERY_KEY]);
        },
        onError: (error) => {
            if (error?.response?.status === NOT_FOUND_STATUS) {
                queryClient.invalidateQueries([TELE_OBJECT_GRID_QUERY_KEY]);
            }
            enqueueSnackbar(extractErrorMessage(error, "Não foi possível salvar o teleobjeto."), {variant: "error"});
        }
    });
};

const deleteTeleObjects = async (ids: Array<number>) => {
    return await axios.delete(apiUrl, {data: ids, timeout: 30000}).then((res) => res.data);
};

export const DeleteTeleObjectReactQuery = () => {
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation<unknown, ApiError, Array<number>>({
        mutationFn: (ids: Array<number>) => {
            return deleteTeleObjects(ids);
        },
        onSuccess: () => {
            enqueueSnackbar("Teleobjeto excluído com sucesso.", {variant: "success"});
            queryClient.invalidateQueries([TELE_OBJECT_GRID_QUERY_KEY]);
        },
        onError: (error, ids) => {
            if (error?.response?.status === NOT_FOUND_STATUS) {
                queryClient.invalidateQueries([TELE_OBJECT_GRID_QUERY_KEY]);
            }
            enqueueSnackbar(extractDeleteErrorMessage(error, ids.length), {
                variant: "error",
                autoHideDuration: DELETE_ERROR_DURATION
            });
        }
    });
};
